import { useState } from 'react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Lock } from 'lucide-react';

/**
 * Modal para cambio obligatorio de contraseña temporal
 * Se muestra cuando un usuario tiene must_change_password=true
 */
export default function ChangePasswordModal({ isOpen, onSuccess }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const validatePassword = password => {
    if (password.length < 8) {
      return 'La contraseña debe tener al menos 8 caracteres';
    }
    if (!/[A-Z]/.test(password)) {
      return 'La contraseña debe incluir al menos una mayúscula';
    }
    if (!/[a-z]/.test(password)) {
      return 'La contraseña debe incluir al menos una minúscula';
    }
    if (!/[0-9]/.test(password)) {
      return 'La contraseña debe incluir al menos un número';
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return 'La contraseña debe incluir al menos un carácter especial (!@#$%^&*...)';
    }
    return null;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const validationError = validatePassword(newPassword);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) throw updateError;

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No se pudo obtener el usuario');

      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({ must_change_password: false })
        .eq('id', user.id);

      if (profileError) throw profileError;

      toast.success('✅ Contraseña actualizada exitosamente');

      setTimeout(() => {
        onSuccess();
      }, 1000);
    } catch (error) {
      console.error('Error al cambiar contraseña:', error);
      toast.error('Error al actualizar la contraseña: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-panel modal-sm" onClick={e => e.stopPropagation()}>
        <div
          className="modal-header"
          style={{
            background: 'linear-gradient(to right, #d97706, #ea580c)',
            color: 'white',
            borderBottom: 'none',
          }}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Lock className="w-6 h-6" />
            </div>
            <div>
              <h2 className="modal-header-title" style={{ color: 'white' }}>
                Cambio de Contraseña Requerido
              </h2>
              <p className="modal-header-sub" style={{ color: '#fef3c7' }}>
                Por tu seguridad, actualiza tu contraseña temporal
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body space-y-6">
            {/* Alerta informativa */}
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
              <p className="text-sm text-amber-800">
                <strong>⚠️ Importante:</strong> Debes cambiar tu contraseña
                temporal antes de continuar usando el sistema.
              </p>
            </div>

            {/* Nueva contraseña */}
            <div>
              <label
                htmlFor="newPassword"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Nueva Contraseña
              </label>
              <div className="relative">
                <input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent pr-12"
                  placeholder="Mínimo 8 caracteres"
                  required
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirmar contraseña */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Confirmar Contraseña
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent pr-12"
                  placeholder="Confirma tu nueva contraseña"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showConfirm ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Requisitos de contraseña */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-xs font-semibold text-gray-700 mb-2">
                Tu contraseña debe incluir:
              </p>
              <ul className="text-xs text-gray-600 space-y-1">
                <li className="flex items-center gap-2">
                  <span
                    className={
                      newPassword.length >= 8
                        ? 'text-green-600'
                        : 'text-gray-400'
                    }
                  >
                    ✓
                  </span>
                  Mínimo 8 caracteres
                </li>
                <li className="flex items-center gap-2">
                  <span
                    className={
                      /[A-Z]/.test(newPassword)
                        ? 'text-green-600'
                        : 'text-gray-400'
                    }
                  >
                    ✓
                  </span>
                  Al menos una mayúscula (A-Z)
                </li>
                <li className="flex items-center gap-2">
                  <span
                    className={
                      /[a-z]/.test(newPassword)
                        ? 'text-green-600'
                        : 'text-gray-400'
                    }
                  >
                    ✓
                  </span>
                  Al menos una minúscula (a-z)
                </li>
                <li className="flex items-center gap-2">
                  <span
                    className={
                      /[0-9]/.test(newPassword)
                        ? 'text-green-600'
                        : 'text-gray-400'
                    }
                  >
                    ✓
                  </span>
                  Al menos un número (0-9)
                </li>
                <li className="flex items-center gap-2">
                  <span
                    className={
                      /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)
                        ? 'text-green-600'
                        : 'text-gray-400'
                    }
                  >
                    ✓
                  </span>
                  Al menos un carácter especial (!@#$%...)
                </li>
              </ul>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full"
              style={{
                background: 'linear-gradient(to right, #d97706, #ea580c)',
                borderColor: 'transparent',
              }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Actualizando...
                </span>
              ) : (
                'Cambiar Contraseña'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
